import { Divider } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButton from '../../components/custom_components/Button'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { PerPaymentCycleDropDown } from '../../components/custom_components/PaymentCycleDropDown'
import { TargetsModal } from '../../components/custom_components/TargetsModal/TargetsModal'
import { useTargetsModalStore } from '../../components/custom_components/TargetsModal/TargetsModalStore'
import { TargetModalType } from '../../components/custom_components/TargetsModal/types'
import PerText from '../../components/custom_components/Text'
import useManagerMeetingStepTargets from '../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import useTargetBonusAndContractChange from '../../graphql/contract/hooks/useTargetBonusAndContractChange'
import useSaveOrUpdateObjectivePayment from '../../graphql/objectivePayments/hooks/useSaveOrUpdateObjectivePayment'
import {
  FullMeetingFragment,
  GetManagerMeetingStepTargetsQuery,
  MeetingType,
  ObjectivePayment,
  ObjectivePaymentInput,
  ObjectiveStatus,
  ObjectiveDto,
  TargetBonusAndContractChangeQuery,
  FindBonusByEmployeeIdInAppraisalSeasonOfMeetingQuery,
  FindPastYearFlexibleBonusByEmployeeIdAndMeetingIdQuery,
  Currency,
} from '../../types'
import { calculatePastTargetsData, calculateNewTargetsData } from '../appraisal/appraisal-flow/shared/util'
import { filterObjectivesByTypeStatusAndRefYear } from '../appraisal/okr/util'
import { ManualDeductionModal } from './ManualDeductionModal'
import { PlannedPaymentTable } from './PlannedPaymentTable'
import {
  useFindBonusByEmployeeIdAndMeetingId,
  useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId,
} from '../appraisal/appraisal-flow/shared/appraisal-components/useBonusAmountEditableIsEnabled'
import {
  getFlexibleBonusUnknownCurrencyForConversion,
  toEligibleFlexibleBonusOverride,
} from '../appraisal/appraisal-flow/shared/flexibleBonusUtil'
import { retrieveContractStartAndEndDateFromContract } from '../profile/contract/utils/contractUtil'
import { retrieveCurrencyConversionRate } from '../../shared/currencyConversionUtil'
import { useCurrency } from '../../context/currency-context'
import { isDefined } from '../../types.helper'

interface PlannedPaymentContainerProps {
  employeeId: string
  showNotOnFirstOfMonthDisclaimer: boolean
  meeting: FullMeetingFragment
  earliestPaymentCycle: string
  objectivePayment: ObjectivePayment
  objectives: ObjectiveDto[]
  handleLowTag: boolean
}

interface PlannedPaymentContainerContentProps {
  employeeId: string
  showNotOnFirstOfMonthDisclaimer: boolean
  meeting: FullMeetingFragment
  earliestPaymentCycle: string
  objectivePayment: ObjectivePayment
  objectives: ObjectiveDto[]
  handleLowTag: boolean
  targetBonusAndContractChangeData: TargetBonusAndContractChangeQuery
  newBonusAmountData: FindBonusByEmployeeIdInAppraisalSeasonOfMeetingQuery
  pastYearBonusAmountData?: FindPastYearFlexibleBonusByEmployeeIdAndMeetingIdQuery
  managerMeetingStepPastTargetsData: GetManagerMeetingStepTargetsQuery
  managerMeetingStepNewTargetsData: GetManagerMeetingStepTargetsQuery
}

export const PlannedPaymentContainer: FC<PlannedPaymentContainerProps> = ({
  employeeId,
  showNotOnFirstOfMonthDisclaimer,
  meeting,
  earliestPaymentCycle,
  objectivePayment,
  objectives,
}) => {
  const { pastTargetsReferenceYear, newTargetsReferenceYear } = meeting.targetsCalculationHelper

  const targetBonusAndContractChange = useTargetBonusAndContractChange(
    employeeId,
    pastTargetsReferenceYear,
    meeting.type === MeetingType.Appraisal,
  )

  const findNewBonusAmount = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)
  const findPastYearBonusAmount = useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId(
    meeting.employee.id,
    meeting.id,
    meeting.type !== MeetingType.Appraisal,
  )

  const managerMeetingStepPastTargets = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    pastTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
    meeting.type === MeetingType.Appraisal,
  )
  const managerMeetingStepNewTargets = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    newTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
  )

  if (
    targetBonusAndContractChange.loading ||
    findNewBonusAmount.loading ||
    findPastYearBonusAmount.loading ||
    managerMeetingStepPastTargets.loading ||
    managerMeetingStepNewTargets.loading ||
    targetBonusAndContractChange.error ||
    findNewBonusAmount.error ||
    findPastYearBonusAmount.error ||
    managerMeetingStepPastTargets.error ||
    managerMeetingStepNewTargets.error ||
    !targetBonusAndContractChange.data ||
    !findNewBonusAmount.data ||
    !managerMeetingStepPastTargets.data ||
    !managerMeetingStepNewTargets.data
  ) {
    return (
      <LoadingAndErrors
        isLoading={
          targetBonusAndContractChange.loading ||
          findPastYearBonusAmount.loading ||
          managerMeetingStepPastTargets.loading ||
          managerMeetingStepNewTargets.loading
        }
        hasNoData={!targetBonusAndContractChange.data || !managerMeetingStepPastTargets.data || !managerMeetingStepNewTargets.data}
        error={
          targetBonusAndContractChange.error ||
          findPastYearBonusAmount.error ||
          managerMeetingStepPastTargets.error ||
          managerMeetingStepNewTargets.error
        }
        showSpinner
      />
    )
  } else {
    return (
      <PlannedPaymentContainerContent
        employeeId={employeeId}
        showNotOnFirstOfMonthDisclaimer={showNotOnFirstOfMonthDisclaimer}
        meeting={meeting}
        earliestPaymentCycle={earliestPaymentCycle}
        objectivePayment={objectivePayment}
        objectives={objectives}
        handleLowTag={meeting.requiresSpecialHandlingOfLowTag}
        managerMeetingStepPastTargetsData={managerMeetingStepPastTargets.data}
        managerMeetingStepNewTargetsData={managerMeetingStepNewTargets.data}
        newBonusAmountData={findNewBonusAmount.data}
        pastYearBonusAmountData={findPastYearBonusAmount.data}
        targetBonusAndContractChangeData={targetBonusAndContractChange.data}
      />
    )
  }
}

const PlannedPaymentContainerContent: FC<PlannedPaymentContainerContentProps> = ({
  meeting,
  showNotOnFirstOfMonthDisclaimer,
  earliestPaymentCycle,
  objectivePayment,
  objectives,
  managerMeetingStepPastTargetsData,
  newBonusAmountData,
  pastYearBonusAmountData,
  managerMeetingStepNewTargetsData,
  targetBonusAndContractChangeData,
}) => {
  const [t] = useTranslation()
  const [showDeductionModal, setShowDeductionModal] = useState(false)

  const [isModalReferringToPastTarget, setIsModalReferringToPastTarget] = useState(true)

  const currentContract = managerMeetingStepPastTargetsData.currentContract!

  const { currencyConversionData } = useCurrency()

  const [objectivePaymentInput, setObjectivePaymentInput] = useState<ObjectivePaymentInput>({
    id: objectivePayment.id,
    paymentDate: objectivePayment.paymentDate,
    deduction: objectivePayment.deduction,
    deductionReason: objectivePayment.deductionReason,
    meetingId: objectivePayment.meetingId,
  })

  const [setModalProps, cleanModalProps, openTargetModal] = useTargetsModalStore(
    (state) => [state.setModalProps, state.cleanModalProps, state.openTargetModal],
    shallow,
  )

  const isAppraisalUpdate = meeting.type === MeetingType.AppraisalUpdate
  const { pastTargetsReferenceYear, newTargetsReferenceYear } = meeting.targetsCalculationHelper

  const saveOrUpdateObjectivePayment = useSaveOrUpdateObjectivePayment(meeting.employee.id, meeting.id, pastTargetsReferenceYear)

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)
  const pastYearFlexibleBonus = pastYearBonusAmountData?.findPastYearFlexibleBonusByEmployeeIdAndMeetingId
  const pastYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    pastYearFlexibleBonus,
    currentContract.currencyForPayments,
    currencyConversionData,
    managerMeetingStepPastTargetsData.branchOfficeByEmployee.timezone,
    pastTargetsReferenceYear,
    managerMeetingStepPastTargetsData.contractsInYear,
    managerMeetingStepPastTargetsData.targetBonusOfYear,
    managerMeetingStepPastTargetsData.contractChangesByEmployee,
  )
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    pastYearFlexibleBonus,
    currentContract.currencyForPayments,
    currencyConversionData,
  )
  const { openTargetBonus, targetBonusAmount } = calculatePastTargetsData(
    managerMeetingStepPastTargetsData.branchOfficeByEmployee.timezone,
    managerMeetingStepPastTargetsData.isBonusValidationEnabled,
    managerMeetingStepPastTargetsData.targetBonusOfYear,
    targetBonusAndContractChangeData.salaryOfYear,
    currentContract.annualSalary ? currentContract.annualSalary.amount : 0,
    currentContract.targetBonus ? currentContract.targetBonus.amount : 0,
    managerMeetingStepPastTargetsData.contractChangesByEmployee,
    managerMeetingStepPastTargetsData.objectivesInMeeting,
    currencyConversionData,
    currentContract.currencyForPayments,
    pastTargetsReferenceYear,
    contractStartDate,
    contractEndDate,
    managerMeetingStepPastTargetsData.contractsInYear,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
  )

  const [newContractStartDate, newContractEndDate] = retrieveContractStartAndEndDateFromContract(
    managerMeetingStepNewTargetsData.currentContract,
  )

  const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed

  const currentYearFlexibleBonus = newBonusAmountData.findBonusByEmployeeIdInAppraisalSeasonOfMeeting
  const currentYearFlexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    currentYearFlexibleBonus,
    currentContract.currencyForPayments,
    currencyConversionData,
  )
  const currentYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    currentYearFlexibleBonus,
    currentContract.currencyForPayments,
    currencyConversionData,
    managerMeetingStepNewTargetsData.branchOfficeByEmployee.timezone,
    newTargetsReferenceYear,
    managerMeetingStepNewTargetsData.contractsInYear,
    managerMeetingStepNewTargetsData.targetBonusOfYear,
    managerMeetingStepNewTargetsData.contractChangesByEmployee,
  )
  const newTargetsData = calculateNewTargetsData(
    allowedNewTargets,
    managerMeetingStepNewTargetsData.branchOfficeByEmployee.timezone,
    managerMeetingStepNewTargetsData.isBonusValidationEnabled,
    currentYearFlexibleBonusOverride,
    managerMeetingStepNewTargetsData.objectivesInMeeting,
    managerMeetingStepNewTargetsData.targetBonusOfYear,
    managerMeetingStepNewTargetsData.salaryOfYear,
    currentContract.currencyForPayments,
    managerMeetingStepNewTargetsData.currentContract!.annualSalary
      ? managerMeetingStepNewTargetsData.currentContract!.annualSalary.amount
      : 0,
    managerMeetingStepNewTargetsData.currentContract!.targetBonus
      ? managerMeetingStepNewTargetsData.currentContract!.targetBonus.amount
      : 0,
    managerMeetingStepNewTargetsData.contractChangesByEmployee,
    isAppraisalUpdate,
    newTargetsReferenceYear,
    newContractStartDate,
    currencyConversionData,
    newContractEndDate,
    managerMeetingStepNewTargetsData.contractsInYear,
    currentYearFlexibleBonusUnknownCurrency,
  )

  useEffect(() => {
    setObjectivePaymentInput({
      id: objectivePayment.id,
      paymentDate: objectivePayment.paymentDate,
      deduction: objectivePayment.deduction,
      deductionReason: objectivePayment.deductionReason,
      meetingId: objectivePayment.meetingId,
    })
  }, [objectivePayment])

  useEffect(() => {
    isModalReferringToPastTarget
      ? setModalProps({
          employeeId: meeting.employee.id,
          meetingId: meeting.id,
          isAppraisalUpdate,
          targetBonus: targetBonusAmount,
          alreadyPlanned: openTargetBonus,
          pastTargetsPaymentView: true,
          employeeCurrency: managerMeetingStepPastTargetsData.currentContract
            ? managerMeetingStepPastTargetsData.currentContract.currencyForPayments
            : null,
          allowedPastTargets: meeting.allowedMonetaryTargets.pastTargetsAllowed,
          allowedNewTargets,
        })
      : setModalProps({
          employeeId: meeting.employee.id,
          meetingId: meeting.id,
          isAppraisalUpdate,
          hasBonusOrWillHaveBonus: newTargetsData.hasBonusOrWillHaveBonus,
          targetBonus: newTargetsData.targetBonusOfTheYear,
          alreadyPlanned: newTargetsData.alreadyPlannedAndPaid,
          employeeCurrency: managerMeetingStepNewTargetsData.currentContract
            ? managerMeetingStepNewTargetsData.currentContract.currencyForPayments
            : null,
          employeeCountry: meeting.employee.currentContract ? meeting.employee.currentContract.legalEntity.country : null,
          allowedPastTargets: meeting.allowedMonetaryTargets.pastTargetsAllowed,
          allowedNewTargets,
          meeting,
        })
    return () => {
      cleanModalProps()
    }
  }, [
    isModalReferringToPastTarget,
    isAppraisalUpdate,
    meeting.employee.id,
    meeting.id,
    meeting.requiresSpecialHandlingOfLowTag,
    setModalProps,
    targetBonusAmount,
    openTargetBonus,
    cleanModalProps,
    meeting,
    newTargetsData.hasBonusOrWillHaveBonus,
    newTargetsData.targetBonusOfTheYear,
    newTargetsData.alreadyPlannedAndPaid,
    currentContract,
    newTargetsReferenceYear,
    managerMeetingStepPastTargetsData.currentContract,
    managerMeetingStepNewTargetsData.objectivesInMeeting,
    managerMeetingStepNewTargetsData.currentContract,
    allowedNewTargets,
  ])

  const objectivesToBePaid = filterObjectivesByTypeStatusAndRefYear(objectives, true, [
    ObjectiveStatus.Approved,
    ObjectiveStatus.PastApproved,
  ]).filter((x) => !!x.achievedAmount)
  const objectivesToBePaidExist = objectivesToBePaid.length !== 0

  const handlePaymentDateChange = async (value: string) => {
    const updatedObjectivePaymentInput = { ...objectivePaymentInput, paymentDate: value }
    setObjectivePaymentInput(updatedObjectivePaymentInput)
    await saveOrUpdateObjectivePayment.createOrUpdateObjectivePayment(updatedObjectivePaymentInput)
  }

  const deleteManualDeduction = () => {
    saveOrUpdateObjectivePayment.createOrUpdateObjectivePayment({
      id: objectivePayment.id,
      meetingId: objectivePayment.meetingId,
      paymentDate: objectivePayment.paymentDate,
      deduction: null,
      deductionReason: null,
    })
  }

  const unknownCurrenciesForConversion: Currency[] = [
    ...new Set(
      [
        ...objectives
          .filter((obj): boolean => {
            if (isDefined(obj.targetAmount) && obj.currency! !== currentContract.currencyForPayments) {
              const conversionRate: number | undefined = retrieveCurrencyConversionRate(
                obj.currency!,
                currentContract.currencyForPayments,
                currencyConversionData,
              )
              if (!conversionRate) {
                return true
              }
            }
            return false
          })
          .map((obj) => obj.currency!),
        ...(flexibleBonusUnknownCurrency ? [flexibleBonusUnknownCurrency] : []),
      ].filter((currency): currency is Currency => !!currency),
    ),
  ]

  const targetBonusToBePaid = !!unknownCurrenciesForConversion.length
    ? 0
    : Number(
        objectivesToBePaid
          .reduce(
            (sum, obj) =>
              obj.achievedAmount
                ? obj.currency! !== currentContract.currencyForPayments
                  ? sum +
                    Number(
                      (
                        obj.achievedAmount *
                        retrieveCurrencyConversionRate(obj.currency!, currentContract.currencyForPayments, currencyConversionData)!
                      ).toFixed(2),
                    )
                  : sum + obj.achievedAmount
                : sum,
            0,
          )
          .toFixed(2),
      )
  return (
    <>
      <>
        {currentContract.isDeductionEnabled && (
          <ManualDeductionModal
            meeting={meeting}
            objectivePayment={objectivePayment}
            targetBonusToBePaid={targetBonusToBePaid}
            visible={showDeductionModal}
            closeModal={() => setShowDeductionModal(false)}
          />
        )}
        <PerFlexRow justify="space-between" align="middle" mb="s">
          <PerFlexRow align="middle">
            <PerText appearance="heading" color="primary" mr={objectivesToBePaidExist ? 's' : undefined}>
              {t('appraisalMeeting.stepPayment.title') + (objectivesToBePaidExist ? ' ' + t('appraisalMeeting.stepPayment.titleOn') : '')}
            </PerText>

            {objectivesToBePaidExist && (
              <PerPaymentCycleDropDown
                value={objectivePaymentInput.paymentDate}
                earliestCycle={earliestPaymentCycle}
                onChange={handlePaymentDateChange}
              />
            )}
          </PerFlexRow>

          {objectivesToBePaidExist && !objectivePayment.deduction && currentContract.isDeductionEnabled && (
            <PerButton
              text={t('appraisalMeeting.stepPayment.deduction.btn')}
              onClick={() => setShowDeductionModal(true)}
              color="primary"
              variant="pastel"
              before="add_stroke"
              data-e2e-edit-objective-payment-add-deduction
              disabled={!!unknownCurrenciesForConversion.length}
              tooltip={
                !!unknownCurrenciesForConversion.length
                  ? t('appraisalStatusModal.unknownConversionRate', {
                      currentContractCurrency: currentContract.currencyForPayments,
                      unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                    })
                  : undefined
              }
            />
          )}
        </PerFlexRow>
        <PerText color="secondary" mb="s">
          {showNotOnFirstOfMonthDisclaimer
            ? t('appraisalMeeting.stepPayment.descriptionWithNotOnFirstOfMonthDisclaimer')
            : t('appraisalMeeting.stepPayment.description')}
        </PerText>
        <PlannedPaymentTable
          objectives={objectivesToBePaid}
          manualDeduction={objectivePayment}
          meetingId={meeting.id}
          handleObjectiveClicked={(_, objective) => {
            const isPastObjective = new Date(objective.start).getUTCFullYear() === pastTargetsReferenceYear && !isAppraisalUpdate
            setIsModalReferringToPastTarget(isPastObjective)
            isPastObjective
              ? openTargetModal(
                  TargetModalType.appraisalEditPastTarget,
                  objective,
                  objective && objective.currency
                    ? retrieveCurrencyConversionRate(objective.currency, currentContract.currencyForPayments, currencyConversionData)
                    : undefined,
                )
              : !objective.id
              ? openTargetModal(
                  TargetModalType.appraisalNewTarget,
                  objective,
                  objective && objective.currency
                    ? retrieveCurrencyConversionRate(objective.currency, currentContract.currencyForPayments, currencyConversionData)
                    : undefined,
                )
              : openTargetModal(
                  TargetModalType.appraisalEditActiveTarget,
                  objective,
                  objective && objective.currency
                    ? retrieveCurrencyConversionRate(objective.currency, currentContract.currencyForPayments, currencyConversionData)
                    : undefined,
                )
          }}
          handleDeductionDelete={() => deleteManualDeduction()}
          handleDeductionEdit={() => setShowDeductionModal(true)}
        />
        <Divider />
      </>
      <TargetsModal />
    </>
  )
}
